import {computed, ref} from 'vue'
import { defineStore } from 'pinia'
import { useApiCall } from '@/composables/useApiCall'
import {
  getOrdersListApiCall,
  getOrderItemApiCall,
  createOrderItemApiCall,
  updateOrderItemApiCall,
  sendSupplierOfferApiCall,
} from '@/api/order'
import { camelCase } from '@/utils/adaptResponseForClient'
import type {
  Order,
  OrderPayload,
  OrderStatus,
  SendOrderPayload,
  UpdateOrderPayload
} from '@/types/order'
import type { ApiDataPaginated, ApiPayloadPaginated, GetItemPayload } from '@/types/api'
import type { DefaultError } from '@/types/api/httpError'

export const useOrderStore = defineStore('orders', () => {
  const ordersList = ref<Order[]>([])
  const currentOrder = ref<null | Order>(null)
  const selectedProducts = ref<number[]>([])
  const selectedSuppliers = ref<number[]>([])
  const {
    isLoading: isOrdersListLoading,
    data: getOrdersListData,
    executeApiCall: getOrdersListAction,
  } = useApiCall<ApiDataPaginated<Order[]>, DefaultError, ApiPayloadPaginated>(
    getOrdersListApiCall,
    true,
  )

  const getOrders = async (params?: ApiPayloadPaginated) => {
    let query = params
    if (!query) query = { page: 1, perPage: 30 }

    try {
      await getOrdersListAction(query)
      if (getOrdersListData.value) {
        ordersList.value = getOrdersListData.value.data.map(order => ({
          ...order,
          status: camelCase(order.status) as OrderStatus,
        }))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const {
    isLoading: isOrderItemLoading,
    data: getOrderItemData,
    executeApiCall: getOrderItemAction,
  } = useApiCall<Order, DefaultError, GetItemPayload>(getOrderItemApiCall, true)

  const getOrder = async (params: GetItemPayload) => {
    try {
      await getOrderItemAction(params)
      currentOrder.value = getOrderItemData.value
      return getOrderItemData.value
    } catch (error) {
      console.error(error)
    }
    return null
  }

  const {
    data: createdOrderData,
    executeApiCall: createOrderAction,
    error: createOrderError,
  } = useApiCall<Order, DefaultError, OrderPayload>(createOrderItemApiCall, true)

  const createOrder = async (params: OrderPayload) => {
    try {
      await createOrderAction(params)
      return createdOrderData.value
    } catch {
      if (createOrderError.value) {
        throw createOrderError.value.data
      }
    }
    return null
  }

  const {
    data: updateOrderData,
    executeApiCall: updateOrderAction,
    error: updateOrderError,
  } = useApiCall<Order, DefaultError, UpdateOrderPayload>(updateOrderItemApiCall, true)

  const updateOrder = async (params: UpdateOrderPayload) => {
    try {
      await updateOrderAction(params)
      return updateOrderData.value
    } catch {
      if (updateOrderError.value) {
        throw updateOrderError.value.data
      }
    }
    return null
  }

  const {
    data: sendOrderData,
    executeApiCall: sendOrderAction,
    error: sendOrderError,
    isLoading: isSendOrderLoading,
  } = useApiCall<Order, DefaultError, SendOrderPayload>(sendSupplierOfferApiCall, true)

  const isLoadingSend = computed(() => isSendOrderLoading.value)

  const sendOrder = async (params: GetItemPayload) => {
    try {
      await sendOrderAction({
        id: params.id,
        data: {
          suppliers: selectedSuppliers.value,
          products: selectedProducts.value,
        }
      })
      clearSelected()
      return sendOrderData.value
    } catch {
      if (sendOrderError.value) {
        throw sendOrderError.value.data
      }
    }
    return null
  }

  const clearSelected = () => {
    selectedProducts.value = []
    selectedSuppliers.value = []
  }

  return {
    ordersList,
    isOrdersListLoading,
    isOrderItemLoading,
    getOrders,
    getOrder,
    createOrder,
    updateOrder,
    selectedProducts,
    selectedSuppliers,
    clearSelected,
    sendOrder,
    isLoadingSend,
    currentOrder,
  }
})
